import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';

import { MS_PER_SECOND } from '~constants/session';

import { CommonState, Row } from '~libs/reduxUtils';

import {
  authenticate,
  authenticateHandoff,
  generateSession,
  getSettings,
  login,
  LoginRejectedPayload,
  typePrefix,
} from '~main/actions/login';
import {
  bootstrapSession,
  extendSession,
  fetchSessionStatus,
  reauthSession,
  toSessionTiming,
} from '~main/actions/session';

/** Deadlines are epoch milliseconds on this browser's clock, derived from the API's relative seconds. */
export interface SessionTiming {
  expiresAt: number;
  absoluteExpiresAt: number;
  reauthExpiresAt: number;
  warningSeconds: number;
  idleTimeoutSeconds: number;
  absoluteTimeoutSeconds: number;
  reauthGraceSeconds: number;
}

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

export type SignOutReason = 'expired' | 'signed-out' | 'unverified';
// 'unavailable': the session check failed for a reason other than "no session" (offline, 5xx, auth-unavailable),
// so the cookie may still be valid. PrivateRoute retries instead of sending the user to /login.
export type SessionStatus = 'unknown' | 'active' | 'none' | 'unavailable';
export type ReauthPrompt = 'none' | 'early' | 'required';

interface SystemConfig {
  content?: {
    status?: string;
    heading?: string;
    text?: string;
  };
  last_updated?: string;
}

export interface User {
  id: number;
  first_name: string;
  surname: string;
  accounts_access_enabled: boolean;
  company_id: string;
  email?: string;
  unlocking_permissions?: boolean;
  company: {
    id: number;
    name: string;
    logo: string;
    abn?: string;
    address?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
  };
}

interface LinkedAccount extends User {
  full_name: string;
}

interface AuthState extends CommonState<Row> {
  authenticated: boolean;
  sessionStatus: SessionStatus;
  session: SessionTiming | null;
  signOutReason: SignOutReason | null;
  reauthPrompt: ReauthPrompt;
  forceRedirect: boolean;
  settings: { [key: string]: string | number | boolean | [] };
  defaults?: { [key: string]: string | number | boolean };
  system?: SystemConfig;
  user?: User;
  tags?: string[];
  linkedAccounts?: LinkedAccount[];
  crossOriginToken?: string;
  captchaReason?: string;
}

const initialState: AuthState = {
  loading: 'idle',
  error: null,
  authenticated: false,
  sessionStatus: 'unknown',
  session: null,
  signOutReason: null,
  reauthPrompt: 'none',
  forceRedirect: false,
  rows: [],
  row: { id: -1 },
  settings: {},
  defaults: {},
  system: {},
  tags: [],
};

// Main slice, connecting API actions to redux state.
const slice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    removeAuthenticated(state, action: PayloadAction<SignOutReason | undefined>) {
      state.authenticated = false;
      state.sessionStatus = 'none';
      state.session = null;
      state.signOutReason = action.payload ?? null;
      state.reauthPrompt = 'none';
      // A background request that failed during sign-out must not leave its raw error on the login page.
      state.error = null;
    },
    setForceRedirect(state) {
      state.forceRedirect = true;
    },
    // Fed by the expiry response headers. Ignored while the password prompt is up: in that state the API
    // sends no expiry headers, and the modal owns the deadline.
    setSessionExpiry(state, action: PayloadAction<{ expiresIn: number; absoluteExpiresIn: number }>) {
      if (!state.session || state.reauthPrompt === 'required') return;
      const now = Date.now();
      const { expiresIn, absoluteExpiresIn } = action.payload;
      const expiresAt = now + expiresIn * MS_PER_SECOND;
      const absoluteExpiresAt = now + absoluteExpiresIn * MS_PER_SECOND;
      const graceMs = state.session.reauthGraceSeconds * MS_PER_SECOND;
      const idleBinds = expiresAt < absoluteExpiresAt;
      state.session.expiresAt = expiresAt;
      state.session.absoluteExpiresAt = absoluteExpiresAt;
      state.session.reauthExpiresAt = idleBinds
        ? Math.min(expiresAt, absoluteExpiresAt + graceMs)
        : absoluteExpiresAt + graceMs;
    },
    // Fed by another tab's broadcast, as absolute deadlines.
    applySessionTiming(
      state,
      action: PayloadAction<{ expiresAt: number; absoluteExpiresAt: number; reauthExpiresAt: number }>,
    ) {
      if (!state.session || state.reauthPrompt === 'required') return;
      state.session = { ...state.session, ...action.payload };
    },
    setReauthPrompt(state, action: PayloadAction<ReauthPrompt>) {
      state.reauthPrompt = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      state.authenticated = true;
      state.sessionStatus = 'active';
      state.session = action.payload?.session ? toSessionTiming(action.payload.session) : null;
      state.signOutReason = null;
      state.reauthPrompt = 'none';
      state.user = action.payload.data;
      state.captchaReason = undefined;
    });
    builder.addCase(login.rejected, (state, action) => {
      const payload = action.payload as LoginRejectedPayload | undefined;
      state.loading = 'declined';
      state.error = payload?.message ?? action.error.message ?? 'Unknown error';
      state.captchaReason = payload?.reason;
    });
    builder.addCase(authenticate.fulfilled, (state, action) => {
      state.authenticated = true;
      state.sessionStatus = 'active';
      state.session = action.payload?.session ? toSessionTiming(action.payload.session) : null;
      state.signOutReason = null;
      state.reauthPrompt = 'none';
      state.user = action.payload.data;
    });
    builder.addCase(authenticateHandoff.fulfilled, (state, action) => {
      state.authenticated = true;
      state.sessionStatus = 'active';
      state.session = action.payload?.session ? toSessionTiming(action.payload.session) : null;
      state.signOutReason = null;
      state.reauthPrompt = 'none';
      state.user = action.payload.data;
    });
    builder.addCase(bootstrapSession.fulfilled, (state, action) => {
      state.authenticated = true;
      state.sessionStatus = 'active';
      state.session = toSessionTiming(action.payload);
      state.signOutReason = null;
      state.reauthPrompt = action.payload.reauth_required ? 'required' : 'none';
    });
    builder.addCase(bootstrapSession.rejected, (state, action) => {
      // Only the API saying there is no session (401/403) is "none". A blip must not cost a valid session.
      const status = action.payload?.status;
      state.sessionStatus = status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN ? 'none' : 'unavailable';
    });
    builder.addCase(extendSession.fulfilled, (state, action) => {
      state.session = toSessionTiming(action.payload);
      if (action.payload.reauth_required) state.reauthPrompt = 'required';
    });
    builder.addCase(fetchSessionStatus.fulfilled, (state, action) => {
      state.session = toSessionTiming(action.payload);
      if (action.payload.reauth_required) {
        state.reauthPrompt = 'required';
      } else if (state.reauthPrompt === 'required') {
        // Another tab confirmed the password while this one was waiting.
        state.reauthPrompt = 'none';
      }
    });
    builder.addCase(reauthSession.fulfilled, (state, action) => {
      state.session = toSessionTiming(action.payload);
      state.reauthPrompt = 'none';
    });
    builder.addCase(generateSession.fulfilled, (state, action) => {
      state.crossOriginToken = action.payload.data;
    });
    builder.addCase(getSettings.fulfilled, (state, action) => {
      const { company, user, tags, defaults, system } = action.payload.data;

      state.settings = company;
      state.system = system;
      state.defaults = defaults;
      state.user = user;
      state.tags = tags;
      state.linkedAccounts = user.linked_accounts;
    });
    // Default matching for loading cases, pending when action is being called
    builder.addMatcher(isPending, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.loading = 'pending';
    });
    builder.addMatcher(isFulfilled, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.loading = 'fulfilled';
    });
    builder.addMatcher(isRejected, (state, action) => {
      // login.rejected is handled by the addCase above (preserves captchaReason from payload)
      if (action.type.startsWith(`${typePrefix}/`) && action.type !== login.rejected.type) {
        state.loading = 'declined';
        // Once signed out with a reason, the login page shows that reason. A background request
        // (settings, feature flags) rejected by the same sign-out must not add its raw API error.
        if (!state.authenticated && state.signOutReason) return;
        state.error = action.error.message;
      }
    });
  },
});

export const { removeAuthenticated, setForceRedirect, setSessionExpiry, applySessionTiming, setReauthPrompt } =
  slice.actions;

export default slice.reducer;
